import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of, take, tap } from 'rxjs';
import { HttpBackend, HttpClient } from '@angular/common/http';
import streaksFallback from '../../../assets/themes/streaks-fallback.json';

function bundledDefaults(): Record<string, string> {
  const arr = (streaksFallback as { theme?: { labels?: Array<{ key: string; value: string }> } })
    .theme?.labels;
  if (!Array.isArray(arr)) return {};
  const map: Record<string, string> = {};
  for (const { key, value } of arr) map[key] = value;
  return map;
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private rawHttp: HttpClient;
  private readonly _labels$ = new BehaviorSubject<Record<string, string>>(
    bundledDefaults(),
  );
  readonly labels$ = this._labels$.asObservable();

  constructor(httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  loadLabels(url?: string | null): Observable<Record<string, string> | null> {
    if (!url) return of(null);
    return this.rawHttp.get<Record<string, string>>(url).pipe(
      take(1),
      tap((map) => {
        if (map) this.mergeLabels(map);
      }),
      catchError((err) => {
        console.warn('[Translation] Failed to load labels: ', err);
        return of(null);
      }),
    );
  }

  setLabels(map: Record<string, string>): void {
    this.mergeLabels(map);
  }

  private mergeLabels(map: Record<string, string>): void {
    this._labels$.next({ ...this._labels$.value, ...map });
  }

  translate(key: string): string {
    try {
      return this.getTranslation(key);
    } catch {
      return key;
    }
  }

  translateArgs(key: string, ...args: string[]): string {
    try {
      return this.replaceArgs(this.getTranslation(key), ...args);
    } catch {
      return key;
    }
  }

  getTranslation(key: string): string {
    return this._labels$.value[key] ?? key;
  }

  private replaceArgs(str: string, ...args: string[]): string {
    if (!args.length || !str) return str;
    const map: Record<string, string> = {};
    args.forEach((a, i) => (map[`#args${i}`] = a));
    const re = new RegExp(Object.keys(map).join('|'), 'gi');
    return str.replace(re, (m) => map[m.toLowerCase()]);
  }
}
